import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Seo } from '@/lib/seo';
import { Loader, Mail, Briefcase, ArrowRight } from '@/components/ui/Icons';
import { cn } from '@/lib/cn';

type Template = {
  key: string;
  label: string;
  description: string;
  audience: 'admin' | 'customer';
};

type ListResp = { ok: true; templates: Template[] };
type ShowResp = { ok: true; key: string; subject: string; html: string; text: string };

export default function AdminEmailTemplates() {
  const list = useQuery<ListResp>({
    queryKey: ['email-templates'],
    queryFn: () => api<ListResp>('/admin/email-templates'),
  });

  const [activeKey, setActiveKey] = useState<string | null>(null);

  // Auto-select the first template once the list loads.
  useEffect(() => {
    if (!activeKey && list.data?.templates.length) {
      setActiveKey(list.data.templates[0].key);
    }
  }, [activeKey, list.data]);

  const detail = useQuery<ShowResp>({
    queryKey: ['email-template', activeKey],
    queryFn: () => api<ShowResp>(`/admin/email-templates/${activeKey}`),
    enabled: !!activeKey,
  });

  const [view, setView] = useState<'desktop' | 'tablet' | 'mobile' | 'text'>('desktop');

  return (
    <>
      <Seo title="Email templates" description="Branded transactional email templates" path="/admin/email-templates" noindex />

      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">
            <Mail className="h-3 w-3" /> Templates
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold text-navy-900">Email templates</h1>
          <p className="mt-1 text-sm text-ink-700">
            Pre-written, brand-consistent emails that go out to customers and the admin team.
            Previews use sample data so you can see exactly what each one looks like before it sends.
          </p>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* Sidebar - template list */}
        <aside className="rounded-2xl border border-paper-100 bg-paper-0 p-3 shadow-card">
          {list.isLoading ? (
            <div className="flex items-center gap-2 p-3 text-sm text-ink-700">
              <Loader className="h-4 w-4" /> Loading…
            </div>
          ) : list.isError || !list.data ? (
            <p className="p-3 text-sm text-red-700">Couldn&rsquo;t load templates.</p>
          ) : (
            <ul className="space-y-1">
              {list.data.templates.map((t) => (
                <li key={t.key}>
                  <button
                    type="button"
                    onClick={() => setActiveKey(t.key)}
                    className={cn(
                      'flex w-full items-start gap-2 rounded-md px-3 py-2.5 text-left transition',
                      activeKey === t.key
                        ? 'bg-navy-900 text-paper-0'
                        : 'hover:bg-paper-50',
                    )}
                  >
                    <span className={cn(
                      'mt-1 grid h-5 w-5 flex-none place-items-center rounded-full text-[10px] font-semibold uppercase tracking-wider',
                      t.audience === 'admin'
                        ? activeKey === t.key ? 'bg-gold-400 text-navy-900' : 'bg-gold-400/15 text-gold-700'
                        : activeKey === t.key ? 'bg-paper-0 text-navy-900' : 'bg-navy-100 text-navy-800',
                    )}>
                      {t.audience === 'admin' ? <Briefcase className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block text-sm font-semibold', activeKey === t.key ? 'text-paper-0' : 'text-navy-900')}>
                        {t.label}
                      </span>
                      <span className={cn('mt-0.5 line-clamp-2 block text-xs', activeKey === t.key ? 'text-paper-0/75' : 'text-ink-700')}>
                        {t.description}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Preview pane */}
        <section className="rounded-2xl border border-paper-100 bg-paper-0 shadow-card">
          {!activeKey ? (
            <div className="p-8 text-sm text-ink-700">Select a template on the left to preview it.</div>
          ) : detail.isLoading ? (
            <div className="flex items-center gap-2 p-8 text-sm text-ink-700">
              <Loader className="h-4 w-4" /> Rendering…
            </div>
          ) : detail.isError || !detail.data ? (
            <div className="p-8 text-sm text-red-700">Couldn&rsquo;t render that template.</div>
          ) : (
            <PreviewPane data={detail.data} view={view} onViewChange={setView} />
          )}
        </section>
      </div>
    </>
  );
}

function PreviewPane({
  data,
  view,
  onViewChange,
}: {
  data: ShowResp;
  view: 'desktop' | 'tablet' | 'mobile' | 'text';
  onViewChange: (v: 'desktop' | 'tablet' | 'mobile' | 'text') => void;
}) {
  const widthMap: Record<string, string> = { desktop: '100%', tablet: '720px', mobile: '380px' };
  const srcDoc = useMemo(() => data.html, [data.html]);

  return (
    <>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-paper-100 px-5 py-4">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-700/70">Subject</p>
          <p className="truncate font-display text-base font-semibold text-navy-900">{data.subject}</p>
        </div>
        <div className="flex gap-1 rounded-full border border-paper-100 bg-paper-50 p-1 text-xs font-semibold">
          {(['desktop', 'tablet', 'mobile', 'text'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onViewChange(v)}
              className={cn(
                'rounded-full px-3 py-1.5 transition',
                view === v ? 'bg-navy-900 text-paper-0' : 'text-ink-700 hover:bg-paper-0',
              )}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="bg-paper-50 p-3 sm:p-6">
        {view === 'text' ? (
          <pre className="max-h-[80vh] overflow-auto rounded-md border border-paper-100 bg-paper-0 p-4 font-mono text-xs leading-relaxed text-ink-800 whitespace-pre-wrap">
{data.text}
          </pre>
        ) : (
          <div className="mx-auto rounded-xl border border-paper-100 bg-paper-0 shadow-xs" style={{ maxWidth: widthMap[view] }}>
            <iframe
              title="email preview"
              srcDoc={srcDoc}
              sandbox=""
              className="block min-h-[70vh] w-full rounded-xl"
            />
          </div>
        )}
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-paper-100 px-5 py-3 text-xs text-ink-700">
        <span>Sample data preview</span>
        <ArrowRight className="h-3 w-3" />
        <span>Real customer details replace this when the email actually sends.</span>
      </footer>
    </>
  );
}
